import type { DynamicObjectInterface } from '../types/propType';

/**
 * Creates an array of numbers from start to end (inclusive).
 * @param start - Starting number
 * @param end - Ending number
 * @returns Array of numbers from start to end
 */
export const range = (start: number, end: number): number[] => {
  if (start > end) {
    return [];
  }
  return Array.from({ length: end - start + 1 }, (_, idx) => idx + start);
};

const isOdd = (value: number) => value % 2 !== 0;

/**
 * Generates pagination range with ellipsis for large page counts.
 * @param currentPage - Current page index (0-based)
 * @param totalRecords - Total number of records
 * @param rowsPerPage - Rows per page
 * @param maxSlots - Max slots between arrows (min 5, must be odd)
 * @returns Array of page numbers and ellipsis ('...')
 */
export const getPaginationRange = (
  currentPage: number,
  totalRecords: number,
  rowsPerPage: number,
  maxSlots: number,
): (number | string)[] => {
  const DOTS = '...';
  const totalPageCount = Math.ceil(totalRecords / rowsPerPage);

  // If total pages fit within the slot limit, show all pages
  if (totalPageCount <= maxSlots) {
    return range(1, totalPageCount);
  }
  currentPage = Math.min(Math.max(currentPage + 1, 0), totalPageCount); //code is written assuming 1 based index
  maxSlots = isOdd(maxSlots) ? maxSlots : maxSlots + 1;
  maxSlots = Math.max(5, maxSlots);

  const midIdx = Math.ceil(maxSlots / 2);

  const ellipsisLeft = currentPage > midIdx;
  const ellipsisRight = totalPageCount - currentPage + 1 > midIdx;

  let leftArr = [];
  if (ellipsisLeft) {
    const slotsAvailable = Math.max(maxSlots - (totalPageCount - currentPage) - 3, midIdx - 3); //These are the slots available to left side of currentPage excluding dots and first page

    leftArr.push(1);
    leftArr.push(DOTS);

    for (let i = currentPage - slotsAvailable; i <= currentPage - 1; i++) {
      leftArr.push(i);
    }
  } else {
    leftArr = range(1, currentPage - 1);
  }

  let rightArr = [];
  if (ellipsisRight) {
    const slotsAvailable = Math.max(maxSlots - currentPage - 2, midIdx - 3); //These are the slots available to right side of currentPage excluding dots and last page

    for (let i = currentPage + 1; i <= currentPage + slotsAvailable; i++) {
      rightArr.push(i);
    }
    rightArr.push(DOTS);
    rightArr.push(totalPageCount);
  } else {
    rightArr = range(currentPage + 1, totalPageCount);
  }

  return [...leftArr, currentPage, ...rightArr];
};

/**
 * Calculates pagination updated data when rows per page changes.
 * @param newRowsPerPage - New rows per page value
 * @param totalRecords - Total number of records
 * @param currentPage - Current page index (0-based)
 * @param startRecordIndex - Function to calculate start record index
 * @param endRecordIndex - Optional function to calculate end record index
 * @returns Updated pagination data object
 */
export const calculatePaginationUpdate = (
  newRowsPerPage: number,
  totalRecords: number,
  currentPage: number,
  startRecordIndex: (page: number, rows: number) => number,
  endRecordIndex?: (page: number, rows: number) => number,
): DynamicObjectInterface => {
  const updateData: DynamicObjectInterface = { rowsPerPage: newRowsPerPage };
  const previousPageEnd = endRecordIndex?.(currentPage, newRowsPerPage) || 0;

  if (newRowsPerPage >= totalRecords) {
    updateData['recordStartIndex'] = 0;
    updateData['recordEndIndex'] = totalRecords;
    updateData['currentPage'] = 0;
  } else if (previousPageEnd >= totalRecords) {
    updateData['recordStartIndex'] = startRecordIndex(currentPage - 1, newRowsPerPage);
    updateData['recordEndIndex'] = previousPageEnd;
    updateData['currentPage'] = currentPage > 0 ? currentPage - 1 : currentPage;
  } else {
    updateData['recordStartIndex'] = startRecordIndex(currentPage, newRowsPerPage);
    updateData['recordEndIndex'] = previousPageEnd;
  }

  return updateData;
};
